import type {
  ConversationPlacement,
  ConversationWorkflowRun,
  CrowdexAssistant,
} from '@crowdex/agent-contracts/api';
import type { SidebarThread } from '@/lib/sidebar-threads';

/*
 * What the sidebar (desktop) and the drawer (phone) list (docs/plan-desktop-mobile-redesign.md
 * §2.1, docs/adr-assistants.md): the assistants, the home one first, each its channel's row
 * (the current one expanded on its work), Needs you, the conversations the user pinned (D22)
 * and the conversations outside any assistant, grouped and filtered by the View. Pure, so both
 * surfaces and the tests share it.
 */

/**
 * What a row's leading icon says (D1): its state only, what runs or waits on you (an approval,
 * a question), else idle. Where it runs and its pull request are its markers (`sidebar-rows`),
 * an automation's run its badge.
 */
export type SidebarRowState = 'running' | 'approval' | 'question' | 'idle';
/** A row's small secondary badge: a run of an automation. */
export type SidebarRowBadge = 'automation';
/** How a task, or one of its steps, ended: its leading icon once it no longer runs nor waits. */
export type SidebarRowOutcome = 'done' | 'failed' | 'stopped';

/** How the body groups its conversations (D70), remembered on this device. */
export type SidebarGroupBy = 'assistant' | 'repository' | 'none';
/** Which conversations it shows (D70); anything but `all` shows as a chip (D71). */
export type SidebarShow = 'all' | 'running' | 'pull-request';
export type SidebarView = { groupBy: SidebarGroupBy; show: SidebarShow };

export const DEFAULT_SIDEBAR_VIEW: SidebarView = { groupBy: 'assistant', show: 'all' };

/** What a conversation waits on the user for, from its notifications (`activity-model`). */
export type SidebarWaiting = { kind: 'approval' | 'question'; since: number };

export type SidebarRow = {
  thread: SidebarThread;
  state: SidebarRowState;
  /** What it is, beside its title; `null` for a plain conversation. */
  badge: SidebarRowBadge | null;
  /** Its runs or child conversations ("· 2"). */
  childCount: number;
  /** Since when it waits on the user, for the age shown on waiting rows. */
  waitingSince: number | null;
  /** Its child conversations the list knows (D49), shown under it once unfolded. */
  children?: SidebarRow[];
  /** Whether its children show (the user's chevron, else open on the conversation shown). */
  expanded?: boolean;
  /** The conversation it is a child of; null at the first level. */
  parentConversationId?: string | null;
  /**
   * A task (a workflow's run, docs/adr-assistants.md): its review round, when in a loop. Its
   * children are its steps, shown as compact sub-rows.
   */
  task?: { round: { current: number; max: number } | null };
  /** A step of a task: its name, without its workflow's ("Review"). */
  stepName?: string;
  /** How it ended, a task or a step that no longer runs nor waits. */
  outcome?: SidebarRowOutcome;
  /** A task's steps: one waits on the user, one has something unread (shown while folded). */
  stepsAttention?: { waiting: boolean; unread: boolean };
};

type SidebarNeedsYouEntry = SidebarRow & {
  kind: 'approval' | 'question';
  /** The colour of its assistant, when it has one. */
  assistantColor: string | null;
};

/**
 * A conversation the user pinned (D22), listed in Pinned instead of its place: an assistant's keeps
 * its assistant's name and colour, dimmed beside its title.
 */
export type SidebarPinnedEntry = SidebarRow & {
  assistantName: string | null;
  assistantColor: string | null;
};

export type SidebarAssistantEntry = {
  assistant: CrowdexAssistant;
  /** Its channel, as the list knows it (null until first opened, or not loaded). */
  channel: SidebarThread | null;
  /** What its channel does: runs a turn, waits on the user, else idle. */
  state: SidebarRowState;
  /** Its channel has something the user has not read. */
  unread: boolean;
  /** Its child conversations (all of them, whatever the filter). */
  childCount: number;
  /** Its channel, or one of its conversations, waits on the user. */
  waiting: boolean;
  /** How many of its first level run now (the row's "2 running"), as its Tasks tab counts. */
  running: number;
  expanded: boolean;
  /** The rows shown under it when expanded (the filter applied). */
  children: SidebarRow[];
};

type SidebarRepositoryGroup = { repository: string; rows: SidebarRow[] };

export type SidebarModel = {
  needsYou: SidebarNeedsYouEntry[];
  /**
   * The conversations the user pinned, in the order they were pinned in (the latest first,
   * unless reordered), whatever the grouping; they leave their regular place.
   */
  pinned: SidebarPinnedEntry[];
  /** The assistants, the home one first, whatever the grouping. */
  assistants: SidebarAssistantEntry[];
  /** `groupBy: 'assistant'`: the conversations outside any assistant. */
  conversations: SidebarRow[];
  /** `groupBy: 'repository'`. */
  repositories: SidebarRepositoryGroup[];
  /** `groupBy: 'none'`: every conversation. */
  list: SidebarRow[];
  /** The assistant of the conversation shown (expanded in the list). */
  currentAssistantId: string | null;
};

/**
 * Whether a workflow run works now: running at a step. One begun but at no step yet, or queued,
 * has not begun (the Tasks tab's "next in the plan", "starting"). The sidebar's "N running" and
 * the Tasks tab's count both read it (H7). A task its agent does alone (no workflow) has no
 * step: it works while it is `running`.
 */
export function workflowRunRuns(
  run:
    | (Pick<ConversationWorkflowRun, 'state' | 'step' | 'runId'> &
        Partial<Pick<ConversationWorkflowRun, 'workflowId'>>)
    | null
    | undefined,
): boolean {
  return (
    run != null &&
    run.state === 'running' &&
    (run.step !== null || run.workflowId === null) &&
    run.runId !== 'queued'
  );
}

export function rowState(
  thread: SidebarThread,
  waiting: SidebarWaiting | undefined,
  placement: ConversationPlacement | undefined,
  /** A project's task: its run says whether it runs, as its Tasks tab says it (H7). */
  task = false,
): SidebarRowState {
  if (waiting?.kind === 'question') return 'question';
  if (waiting?.kind === 'approval' || thread.actionRequired === true) return 'approval';
  // An automation's run waiting on its step's reviewer waits on you as any conversation.
  if (placement?.workflowRun?.state === 'waiting') return 'approval';
  if (task) return workflowRunRuns(placement?.workflowRun) ? 'running' : 'idle';
  if (thread.loading === true) return 'running';
  if (placement?.workflowRun?.state === 'running') return 'running';
  return 'idle';
}

/** How a task ended, once it no longer runs nor waits; nothing while queued or under way. */
function taskOutcome(run: ConversationWorkflowRun): SidebarRowOutcome | undefined {
  if (run.state === 'succeeded') return 'done';
  if (run.state === 'failed') return 'failed';
  if (run.state === 'stopped') return 'stopped';
  return undefined;
}

/** How a task's step ended: as its task's plan says it, else as its task did when it succeeded. */
function stepOutcome(id: string, run: ConversationWorkflowRun): SidebarRowOutcome | undefined {
  const item = run.plan?.items.find((entry) => entry.conversationId === id);
  if (item?.state === 'done') return 'done';
  if (item?.state === 'failed') return 'failed';
  if (run.state === 'succeeded') return 'done';
  if (run.state === 'stopped') return 'stopped';
  return undefined;
}

/**
 * A step's name: its title without its workflow's ("Ship: Review" reads "Review"), the workflow
 * being the tag its run gives it.
 */
export function stepName(thread: Pick<SidebarThread, 'title' | 'tags'>): string {
  for (const tag of thread.tags ?? []) {
    const prefix = `${tag}:`;
    if (thread.title.startsWith(prefix)) {
      const rest = thread.title.slice(prefix.length).trim();
      if (rest !== '') return rest;
    }
  }
  return thread.title;
}

/** What a conversation is, as its row's badge: an automation's run (not a task its agent does alone). */
export function rowBadge(placement: ConversationPlacement | undefined): SidebarRowBadge | null {
  return placement?.workflowRun != null && placement.workflowRun.workflowId !== null
    ? 'automation'
    : null;
}

function matchesShow(row: SidebarRow, show: SidebarShow): boolean {
  if (show === 'running') return row.state === 'running';
  if (show === 'pull-request') {
    const pullRequest = row.thread.pullRequest;
    return pullRequest !== null && pullRequest !== undefined;
  }
  return true;
}

export function buildSidebar(input: {
  threads: readonly SidebarThread[];
  assistants: readonly CrowdexAssistant[];
  placements: Readonly<Record<string, ConversationPlacement>>;
  view: SidebarView;
  waiting: ReadonlyMap<string, SidebarWaiting>;
  activeThreadId: string;
  /** Assistants the user opened or closed by hand (their chevron). */
  toggledAssistants?: ReadonlySet<string>;
  /** Parent conversations the user opened or closed by hand (their chevron). */
  toggledParents?: ReadonlySet<string>;
  /** Conversations pinned (D22), in their pinned order (a Set keeps its insertion order). */
  pinnedIds?: ReadonlySet<string>;
}): SidebarModel {
  const { threads, view, waiting, activeThreadId } = input;
  // The directory's placements, else what the thread carries (a linked cloud's conversation).
  const placements: Record<string, ConversationPlacement> = { ...input.placements };
  for (const thread of threads)
    if (placements[thread.id] === undefined && thread.placement !== undefined)
      placements[thread.id] = thread.placement;
  const pinned = input.pinnedIds ?? new Set<string>();
  const toggled = input.toggledAssistants ?? new Set<string>();
  // The home assistant first, the others in the directory's order.
  const liveAssistants = input.assistants
    .filter((project) => project.archivedAt === null)
    .sort((left, right) => Number(right.isHome === true) - Number(left.isHome === true));
  const projectsById = new Map(liveAssistants.map((project) => [project.id, project]));
  // Each assistant's channel (none until it is first opened).
  const projectConversations = new Map(
    liveAssistants.flatMap((project) =>
      project.conversationId === null ? [] : [[project.conversationId, project] as const],
    ),
  );
  const childCounts = new Map<string, number>();
  for (const placement of Object.values(placements)) {
    const parent = placement.parentConversationId;
    if (parent !== null) childCounts.set(parent, (childCounts.get(parent) ?? 0) + 1);
  }
  const toRow = (thread: SidebarThread): SidebarRow => {
    const placement = placements[thread.id];
    const waitingEntry = waiting.get(thread.id);
    // A task: a workflow's run (its placement carries it); its run says what it does (H7).
    const run = placement?.workflowRun ?? null;
    const state = rowState(thread, waitingEntry, placement, run !== null);
    const parent = placement?.parentConversationId ?? null;
    const row: SidebarRow = {
      thread,
      state,
      badge: rowBadge(placement),
      childCount: childCounts.get(thread.id) ?? 0,
      waitingSince:
        state === 'approval' || state === 'question'
          ? (waitingEntry?.since ?? thread.updatedAt)
          : null,
      parentConversationId: parent,
    };
    if (run !== null) {
      row.task = { round: run.round };
      const outcome = state === 'idle' ? taskOutcome(run) : undefined;
      if (outcome !== undefined) row.outcome = outcome;
    }
    // A step: a child of a task (one per agent step, loop round or item of its run).
    const parentRun = parent === null ? null : (placements[parent]?.workflowRun ?? null);
    if (parentRun !== null) {
      row.stepName = stepName(thread);
      const outcome = state === 'idle' ? stepOutcome(thread.id, parentRun) : undefined;
      if (outcome !== undefined) row.outcome = outcome;
    }
    return row;
  };
  // The latest activity first (no sort to choose, D70).
  const ordered = (rows: SidebarRow[]) =>
    rows.sort((left, right) => right.thread.updatedAt - left.thread.updatedAt);

  // Channels are their assistants' rows, never listed as conversations.
  const conversations = threads.filter((thread) => !projectConversations.has(thread.id));
  const rows = conversations.map(toRow);
  nestChildren(rows, projectConversations, activeThreadId, input.toggledParents, ordered, pinned);

  const needsYou: SidebarNeedsYouEntry[] = ordered(
    rows.filter((row) => row.state === 'approval' || row.state === 'question'),
  ).map((row) => {
    const projectId = placements[row.thread.id]?.projectId ?? null;
    return {
      ...row,
      kind: row.state === 'question' ? 'question' : 'approval',
      assistantColor: projectId === null ? null : (projectsById.get(projectId)?.color ?? null),
    };
  });

  // A conversation's children show under it (their count), not beside it: a task's steps under
  // their task, never at the first level. A child whose parent the list does not hold (archived,
  // not loaded) stays hidden; search finds it.
  const listed = new Set(rows.map((row) => row.thread.id));
  const firstLevel = rows.filter((row) => {
    const parent = row.parentConversationId ?? null;
    return parent === null || projectConversations.has(parent);
  });
  // What the first level does not show: steps, and children of a parent it does not hold.
  const tucked = rows.filter((row) => {
    const parent = row.parentConversationId ?? null;
    return (
      parent !== null &&
      !projectConversations.has(parent) &&
      (row.stepName !== undefined || !listed.has(parent))
    );
  });
  // A step that waits on the user or has something unread marks its task (and its assistant).
  for (const row of rows) {
    if (row.task === undefined) continue;
    const steps = tucked.filter((step) => step.parentConversationId === row.thread.id);
    const waits = steps.some((step) => step.state === 'approval' || step.state === 'question');
    const unread = steps.some((step) => step.thread.unread === true);
    if (waits || unread) row.stepsAttention = { waiting: waits, unread };
  }
  // A pinned conversation is listed in Pinned only, wherever it would stand.
  const topLevel = firstLevel.filter((row) => !pinned.has(row.thread.id));
  // A filter keeps a parent when one of its descendants matches, and narrows its children,
  // unfolded on what matches.
  const narrowed = (row: SidebarRow): SidebarRow | null => {
    if (view.show === 'all') return row;
    const children = (row.children ?? []).flatMap((child) => narrowed(child) ?? []);
    if (!matchesShow(row, view.show) && children.length === 0) return null;
    if (row.children === undefined) return row;
    const opened = children.length > 0;
    return {
      ...row,
      children,
      expanded: input.toggledParents?.has(row.thread.id) === true ? !opened : opened,
    };
  };
  const shown = topLevel.flatMap((row) => narrowed(row) ?? []);

  const rowsById = new Map(rows.map((row) => [row.thread.id, row]));
  const pinnedRows: SidebarPinnedEntry[] = [...pinned].flatMap((id) => {
    const found = rowsById.get(id);
    const row = found === undefined ? null : narrowed(found);
    if (row === null) return [];
    const project = projectsById.get(placements[id]?.projectId ?? '') ?? null;
    return [
      { ...row, assistantName: project?.name ?? null, assistantColor: project?.color ?? null },
    ];
  });

  const activePlacement = placements[activeThreadId];
  const currentAssistantId =
    projectConversations.get(activeThreadId)?.id ??
    (activePlacement?.projectId !== undefined &&
    activePlacement.projectId !== null &&
    projectsById.has(activePlacement.projectId)
      ? activePlacement.projectId
      : null);

  const threadsById = new Map(threads.map((thread) => [thread.id, thread]));
  const assistants: SidebarAssistantEntry[] = liveAssistants.map((project) => {
    const channel =
      project.conversationId === null ? null : (threadsById.get(project.conversationId) ?? null);
    const channelState =
      channel === null
        ? 'idle'
        : rowState(channel, waiting.get(channel.id), placements[channel.id]);
    // Its pinned conversations count, though they show in Pinned; its tasks' steps do not.
    const all = firstLevel.filter((row) => placements[row.thread.id]?.projectId === project.id);
    const ownTucked = tucked.filter((row) => placements[row.thread.id]?.projectId === project.id);
    const children = ordered(
      all.flatMap((row) => (pinned.has(row.thread.id) ? [] : (narrowed(row) ?? []))),
    );
    // A filter shows what matches under every project (D71); else only the current one opens.
    const defaultExpanded =
      view.show !== 'all' ? children.length > 0 : project.id === currentAssistantId;
    return {
      assistant: project,
      channel,
      state: channelState,
      unread: channel?.unread === true || ownTucked.some((row) => row.thread.unread === true),
      childCount: all.length,
      waiting:
        channelState === 'approval' ||
        channelState === 'question' ||
        [...all, ...ownTucked].some((row) => row.state === 'approval' || row.state === 'question'),
      // Its first level that runs: what its Running filter shows, its rows' rings say and
      // its Tasks tab counts (a task runs as its run does; its steps are no tasks), H7.
      running: all.filter((row) => row.state === 'running').length,
      expanded: toggled.has(project.id) ? !defaultExpanded : defaultExpanded,
      children,
    };
  });

  const outside = ordered(
    shown.filter((row) => {
      const projectId = placements[row.thread.id]?.projectId ?? null;
      return projectId === null || !projectsById.has(projectId);
    }),
  );

  // By repository identity (two repositories of one name stay apart), shown by their label.
  const byRepository = new Map<string, { repository: string; rows: SidebarRow[] }>();
  for (const row of ordered([...shown])) {
    const key = row.thread.projectKey ?? row.thread.project;
    const group = byRepository.get(key);
    if (group === undefined) byRepository.set(key, { repository: row.thread.project, rows: [row] });
    else group.rows.push(row);
  }

  return {
    needsYou,
    pinned: pinnedRows,
    assistants,
    conversations: outside,
    repositories: [...byRepository.values()],
    list: ordered([...shown]),
    currentAssistantId,
  };
}

/**
 * Hangs each child conversation under its parent row (D49): the list shows the first level, a
 * parent unfolds on its children. A parent opens on its own when the conversation shown is one
 * of its descendants; its chevron (`toggled`) reverses that. Tasks of a project's conversation
 * stay at the project's first level.
 */
function nestChildren(
  rows: readonly SidebarRow[],
  projectConversations: ReadonlyMap<string, unknown>,
  activeThreadId: string,
  toggled: ReadonlySet<string> | undefined,
  ordered: (rows: SidebarRow[]) => SidebarRow[],
  /** Pinned conversations stand in Pinned, not under their parent (it still counts them). */
  pinned: ReadonlySet<string>,
): void {
  const byId = new Map(rows.map((row) => [row.thread.id, row]));
  const children = new Map<string, SidebarRow[]>();
  const childCountFloor = new Map<string, number>();
  for (const row of rows) {
    const parent = row.parentConversationId ?? null;
    if (parent === null || projectConversations.has(parent) || !byId.has(parent)) continue;
    childCountFloor.set(parent, (childCountFloor.get(parent) ?? 0) + 1);
    if (pinned.has(row.thread.id)) continue;
    const list = children.get(parent);
    if (list === undefined) children.set(parent, [row]);
    else list.push(row);
  }
  const holdsActive = (row: SidebarRow, seen: Set<string>): boolean => {
    if (seen.has(row.thread.id)) return false;
    seen.add(row.thread.id);
    return (children.get(row.thread.id) ?? []).some(
      (child) => child.thread.id === activeThreadId || holdsActive(child, seen),
    );
  };
  for (const row of rows) {
    // The count says at least what the list knows of it.
    row.childCount = Math.max(row.childCount, childCountFloor.get(row.thread.id) ?? 0);
    const list = children.get(row.thread.id);
    if (list === undefined) continue;
    row.children = ordered(list);
    const opened = holdsActive(row, new Set());
    row.expanded = toggled?.has(row.thread.id) === true ? !opened : opened;
  }
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "now", "8 min", "2 h", "3 d", "5 w": the age shown on waiting rows and on hover. */
export function formatAge(at: number, now = Date.now()): string {
  const elapsed = Math.max(0, now - at);
  if (elapsed < MINUTE) return 'now';
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)} min`;
  if (elapsed < DAY) return `${Math.floor(elapsed / HOUR)} h`;
  if (elapsed < 7 * DAY) return `${Math.floor(elapsed / DAY)} d`;
  return `${Math.floor(elapsed / (7 * DAY))} w`;
}

/** Labels of the View choices (D70), shared by the desktop menu and the phone's sheet. */
export const GROUP_BY_OPTIONS: readonly {
  value: SidebarGroupBy;
  label: string;
  hint?: string;
}[] = [
  { value: 'assistant', label: 'Assistant', hint: 'Assistants, then conversations' },
  { value: 'repository', label: 'Repository' },
  { value: 'none', label: 'None' },
];

export const SHOW_OPTIONS: readonly { value: SidebarShow; label: string; chip: string }[] = [
  { value: 'all', label: 'All conversations', chip: 'All' },
  { value: 'running', label: 'Running', chip: 'Running' },
  { value: 'pull-request', label: 'With a pull request', chip: 'With a PR' },
];
